import client from './client';

export type WhatsAppDisconnectStrategy = 'after_send' | 'keep_10m' | 'manual';
export type WhatsAppAuthMode = 'qr' | 'pairing_code';
export type WhatsAppConnectionStatus =
  | 'not_initialized'
  | 'initializing'
  | 'initializing_browser'
  | 'injecting_page'
  | 'awaiting_qr_or_code'
  | 'recovering'
  | 'qr_pending'
  | 'pairing_code_pending'
  | 'ready'
  | 'connected'
  | 'disconnected'
  | 'need_reauth'
  | 'auth_failure'
  | 'error'
  | 'fatal_error'
  | 'worker_unavailable'
  | 'worker_starting'
  | 'worker_dependency_missing'
  | 'worker_config_invalid'
  | 'worker_lock_conflict'
  | 'worker_degraded';

export interface WhatsAppConnectionState {
  qr: string | null;
  pairing_code: string | null;
  pairing_code_expires_in_ms: number | null;
  connected_phone?: string | null;
  status: WhatsAppConnectionStatus;
  auth_mode: WhatsAppAuthMode;
  message?: string;
  attempt_id?: string | null;
  request_id?: string | null;
}

export interface MyWhatsAppAccountInfo {
  id: string;
  phone_number: string;
  display_name: string | null;
  status: string;
  health_score: number;
  proxy_ip: string | null;
  daily_sent: number;
  daily_limit: number;
  last_active_at: string | null;
  suspended_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface MyWhatsAppAccountBinding {
  id: string;
  user_id: string;
  wa_account_id: string;
  alias: string | null;
  set_default: boolean;
  is_default: boolean;
  status: 'active' | 'disabled';
  sort_order: number;
  last_used_at: string | null;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
  connection_status: 'offline' | 'connecting' | 'online' | 'recovering' | 'need_reauth' | 'suspended' | 'deleting' | 'deleted' | null;
  account: MyWhatsAppAccountInfo;
}

export type WhatsAppBindingDraftStatus =
  | 'pending_verification'
  | 'connecting'
  | 'verified'
  | 'converted'
  | 'expired'
  | 'cancelled'
  | 'rejected_number_mismatch'
  | 'rejected_already_bound'
  | 'failed';

export interface WhatsAppBindingDraft {
  id: string;
  user_id: string;
  requested_phone_number: string;
  normalized_phone_number: string;
  alias: string | null;
  status: WhatsAppBindingDraftStatus;
  verified_account_id: string | null;
  failure_code: string | null;
  failure_message: string | null;
  expires_at: string;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export type WhatsAppConnectionAttemptStatus =
  | 'created'
  | 'queued'
  | 'runtime_allocated'
  | 'client_starting'
  | 'qr_pending'
  | 'pairing_code_pending'
  | 'authenticating'
  | 'verifying_identity'
  | 'ready'
  | 'cancelling'
  | 'cancelled'
  | 'expired'
  | 'failed';

export interface WhatsAppConnectionAttemptEvent {
  id: string;
  attempt_id: string;
  wa_account_id: string | null;
  event_type: string;
  from_status: WhatsAppConnectionAttemptStatus | null;
  to_status: WhatsAppConnectionAttemptStatus | null;
  error_code: string | null;
  message: string | null;
  trace_id: string;
  metadata: Record<string, unknown>;
  duration_ms: number | null;
  created_at: string;
}

export interface WhatsAppConnectionAttempt {
  id: string;
  user_id: string;
  draft_id: string | null;
  wa_account_id: string | null;
  auth_method: WhatsAppAuthMode;
  status: WhatsAppConnectionAttemptStatus;
  failure_code: string | null;
  failure_message: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppConnectionAttemptSnapshot extends WhatsAppConnectionAttempt {
  auth_payload: {
    qr: string | null;
    pairing_code: string | null;
    expires_in_ms: number | null;
  };
  replacement_attempt?: WhatsAppConnectionAttemptSnapshot | null;
}

export const whatsappApi = {
  getMyContacts: () => client.get('/whatsapp/my/contacts'),
  getMySendReceipts: (params?: { campaign_id?: string; wa_account_id?: string; limit?: number }) => client.get('/whatsapp/my/send-receipts', { params }),
  getMyAccounts: () => client.get('/whatsapp/my/accounts'),
  bindMyAccount: (payload: { wa_account_id?: string; phone_number?: string; display_name?: string; alias?: string; set_default?: boolean }) =>
    client.post('/whatsapp/my/accounts', payload),
  updateMyAccountBinding: (bindingId: string, payload: { alias?: string | null; is_default?: boolean }) =>
    client.patch(`/whatsapp/my/accounts/${bindingId}`, payload),
  unbindMyAccount: (bindingId: string) => client.delete(`/whatsapp/my/accounts/${bindingId}`),
  purgeMyAccountBinding: (bindingId: string) => client.post(`/whatsapp/my/accounts/${bindingId}/purge`),
  verifyMyAccountBinding: (bindingId: string) => client.post(`/whatsapp/my/accounts/${bindingId}/verify`),
  listMyBindingDrafts: () => client.get('/whatsapp/my/binding-drafts'),
  createMyBindingDraft: (payload: { phone_number: string; alias?: string; set_default?: boolean }) =>
    client.post('/whatsapp/my/binding-drafts', payload),
  cancelMyBindingDraft: (draftId: string) => client.delete(`/whatsapp/my/binding-drafts/${draftId}`),
  connectMyBindingDraft: (draftId: string, auth_method: WhatsAppAuthMode) =>
    client.post(`/whatsapp/my/binding-drafts/${draftId}/connect`, { auth_method }),
  listMyConnectionAttempts: (params?: { draft_id?: string; wa_account_id?: string }) =>
    client.get('/whatsapp/my/connection-attempts', { params }),
  getMyConnectionAttempt: (attemptId: string) => client.get(`/whatsapp/my/connection-attempts/${attemptId}`),
  cancelMyConnectionAttempt: (attemptId: string) => client.post(`/whatsapp/my/connection-attempts/${attemptId}/cancel`),
  listMyConnectionAttemptEvents: (attemptId: string, params?: { after_id?: number }) =>
    client.get(`/whatsapp/my/connection-attempts/${attemptId}/events`, { params }),
  initSession: (id: string, data?: { auth_mode?: WhatsAppAuthMode; phone_number?: string }) =>
    client.post(`/whatsapp/accounts/${id}/init-session`, data),
  cancelInitSession: (id: string) => client.post(`/whatsapp/accounts/${id}/cancel-init-session`),
  getConnectionState: (id: string) => client.get(`/whatsapp/accounts/${id}/connection-state`),
  getQRCode: (id: string) => client.get(`/whatsapp/accounts/${id}/qrcode`),
  disconnectAccount: (id: string) => client.post(`/whatsapp/accounts/${id}/disconnect`),
  forwardMessage: (data: {
    content: string;
    contact_ids?: string[];
    recipient_numbers?: string[];
    campaign_id?: string;
    media_url?: string;
    wa_account_id?: string;
    disconnect_strategy?: WhatsAppDisconnectStrategy;
    disconnect_after_send?: boolean;
  }) =>
    client.post('/whatsapp/my/forward', data),
};
